import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { AuthService } from "./auth.service";

@Injectable()
export class AuthGuardService {
  private store = inject<Store<any>>(Store);
  router = inject(Router);
  private authService = inject(AuthService);

  isAuthenticated = false;
  isAdmin = false;
  isPlanner = false;

  constructor() {
    const router = this.router;

    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      // Reset on every emission so cleared auth (logout/expiry) revokes
      // access instead of leaving stale flags behind.
      this.isAuthenticated = !!auth && auth.isAuthenticated;
      this.isAdmin = !!auth && auth.isAdmin;
      this.isPlanner = !!auth && auth.isPlanner;
    });

    // Reset cancelled state when navigating to a new route
    router.events.subscribe(() => {
      this.authService.resetCancelledState();
    });
  }

  // Track the last login attempt to prevent multiple simultaneous attempts
  private lastLoginAttempt = 0;
  private loginInProgress = false;

  async canActivate(): Promise<boolean> {
    try {
      // If already authenticated, allow access
      if (this.isAuthenticated) {
        console.log("[AuthGuard] Already authenticated, allowing access");
        return true;
      }

      // Prevent multiple login attempts in quick succession
      const now = Date.now();
      if (this.loginInProgress || now - this.lastLoginAttempt < 3000) {
        console.log(
          "[AuthGuard] Login already in progress or attempted recently, waiting..."
        );

        // Wait briefly to see if authentication completes
        return new Promise((resolve) => {
          setTimeout(async () => {
            // Check if we became authenticated while waiting
            if (this.isAuthenticated) {
              console.log(
                "[AuthGuard] Became authenticated while waiting, allowing access"
              );
              resolve(true);
            } else {
              console.log(
                "[AuthGuard] Still not authenticated after waiting, denying access"
              );
              resolve(false);
            }
          }, 2000);
        });
      }

      // Set login in progress flag
      this.loginInProgress = true;
      this.lastLoginAttempt = now;

      try {
        // Not authenticated, try login
        console.log("[AuthGuard] Not authenticated, attempting login");
        const result = await this.authService.login();

        // Clear login in progress flag
        this.loginInProgress = false;

        if (!result) {
          console.log("[AuthGuard] Login failed or was cancelled");
          // Don't navigate here as AuthService handles navigation
          return false;
        }
        console.log("[AuthGuard] Login successful");
        return true;
      } catch (error) {
        // Clear login in progress flag on error
        this.loginInProgress = false;
        throw error;
      }
    } catch (error) {
      console.error("[AuthGuard] Auth guard error:", error);
      this.authService.login();
      return false;
    }
  }
}
